import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Interaction } from 'src/api/interactions';
import { DataTable } from 'src/components/DataTable';
import { ActionMenu } from 'src/components/DataTable/ActionMenu';
import { Td } from 'src/components/DataTable/Td';
import { Status } from 'src/components/Status';
import { useGlobalModals } from 'src/contexts/global-modals';
import { links } from 'src/router/routes';

const columns = [
  {
    key: 'id',
    label: 'ID',
  },
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'interaction_dictionary',
    label: 'Interaction',
  },
  {
    key: 'status',
    label: 'Status',
  },
  {
    key: 'updated_at',
    label: 'Last updated at',
  },
];

type Props = {
  interactions: Interaction[];
  isLoading: boolean;
};

export const InteractionsDataTable = ({ interactions, isLoading }: Props) => {
  const navigate = useNavigate();

  const { openDeleteInteractionModal } = useGlobalModals();

  const handleRowClick = useCallback(
    (interactionId: number) => {
      navigate(`${links.interaction}/${interactionId}`);
    },
    [navigate]
  );

  const getContent = useCallback(
    (interaction: Interaction) => {
      return (
        <>
          <Td>{interaction.id}</Td>
          <Td>{interaction.name}</Td>
          <Td>{interaction.interaction_dictionary.name}</Td>
          <Td>
            {interaction?.status && <Status status={interaction.status} />}
          </Td>
          <Td>
            <span>{new Date(interaction.updated_at).toLocaleDateString()}</span>
            <ActionMenu
              onEdit={() => navigate(`${links.interaction}/${interaction.id}`)}
              onRemove={() => openDeleteInteractionModal(interaction.id)}
            />
          </Td>
        </>
      );
    },
    [openDeleteInteractionModal, navigate]
  );

  const getRows = useCallback((interaction: Interaction) => {
    return {
      id: String(interaction.id),
      interaction_dictionary: interaction.interaction_dictionary.name,
      name: interaction.name,
      status: interaction.status,
      updated_at: interaction.updated_at,
    };
  }, []);

  const data = useMemo(() => {
    return (
      interactions?.map((interaction) => ({
        content: getContent(interaction),
        onRowClick: () => handleRowClick(interaction.id),
        rows: getRows(interaction),
      })) || []
    );
  }, [interactions, getContent, getRows, handleRowClick]);

  return <DataTable columns={columns} data={data} isLoading={isLoading} />;
};
